import React, { FC } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MoveRight } from "lucide-react";
import { useAppSelector } from "store/hooks/hooks";
import { selectLiveEvents, selectEventsStatus } from "store/slices/eventsSlice";

interface PromptVisualizationProps {
    isMenuOpen: boolean;
    isCategoryMenuOpen: boolean;
    showSearchForm: boolean;
}

const LiveBadge = () => (
    <span className="flex items-center gap-1.5 text-[9px] font-black text-emerald-600 dark:text-emerald-400 tracking-tighter">
        <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        LIVE
    </span>
);

const PromptVisualization: FC<PromptVisualizationProps> = ({
    isMenuOpen,
    isCategoryMenuOpen,
    showSearchForm,
}) => {
    const navigate = useNavigate();
    const liveEvents = useAppSelector(selectLiveEvents) || [];
    const eventsStatus = useAppSelector(selectEventsStatus);

    return (
        <AnimatePresence>
            {eventsStatus === "succeeded" &&
                !isMenuOpen &&
                !isCategoryMenuOpen &&
                !showSearchForm && (
                    <motion.div
                        layout
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 25,
                            duration: 0.5,
                        }}
                        className="w-full flex justify-center px-4 py-1"
                    >
                        <div className="bg-white/90 dark:bg-neutral-900/90 border border-slate-200/60 dark:border-neutral-800/60 shadow-[0_4px_20px_rgba(0,0,0,0.04)] rounded-xl px-1.5 py-1.5 backdrop-blur-xl flex items-center justify-start sm:justify-center gap-2 sm:gap-4 overflow-x-auto no-scrollbar max-w-[calc(100vw-32px)]">
                            {/* Dynamic Event Links (Live Spotlight) */}
                            <div className="flex items-center gap-2 sm:gap-3">
                                {(liveEvents || []).slice(0, 2).map((event, index) => (
                                    <motion.button
                                        key={event.id}
                                        onClick={() =>
                                            navigate(
                                                `/occasions?eventId=${event.id
                                                }&eventName=${encodeURIComponent(event.name)}`
                                            )
                                        }
                                        whileHover="hover"
                                        animate={{
                                            scale: [1, 1.02, 1],
                                        }}
                                        transition={{
                                            scale: {
                                                duration: 3,
                                                repeat: Infinity,
                                                ease: "easeInOut",
                                                delay: index * 0.5
                                            }
                                        }}
                                        className="relative px-3 sm:px-4 py-1 rounded-lg sm:rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 border border-emerald-100/50 dark:border-emerald-900/30 flex items-center gap-2 sm:gap-3 hover:shadow-md transition-all group/item whitespace-nowrap overflow-hidden"
                                    >
                                        <LiveBadge />
                                        <span className="text-[10px] sm:text-[12px] font-extrabold tracking-wide text-gray-800 dark:text-gray-200 group-hover/item:text-emerald-700 transition-colors uppercase">
                                            {event.name}
                                        </span>

                                        {/* Animated Arrow Icon */}
                                        <motion.div
                                            variants={{
                                                hover: { x: 5 }
                                            }}
                                            animate={{
                                                x: [0, 3, 0],
                                            }}
                                            transition={{
                                                x: {
                                                    duration: 2,
                                                    repeat: Infinity,
                                                    ease: "easeInOut",
                                                    delay: 1 + index * 0.5
                                                }
                                            }}
                                            className="text-gray-400 group-hover/item:text-emerald-600 transition-colors"
                                        >
                                            <MoveRight className="w-4 h-4" strokeWidth={3} />
                                        </motion.div>

                                        {/* Shimmer Light Sweep */}
                                        <motion.div
                                            variants={{
                                                hover: {
                                                    x: ["-100%", "200%"],
                                                }
                                            }}
                                            transition={{
                                                duration: 0.8,
                                                ease: "easeInOut",
                                            }}
                                            className="absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-20deg] pointer-events-none"
                                            style={{ x: "-100%" }}
                                        />
                                    </motion.button>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
        </AnimatePresence>
    );
};

export default PromptVisualization;
